import { useEffect } from 'react';
import { Plus } from 'lucide-react';
import { addSubmodel, removeSubmodel, setSubmodelUrl, useSubmodels } from './listingStore';
import { LinkRow } from './ListingUrlField';
import { smallBtnClass } from './controls';
import { useLanguage } from '../../contexts/LanguageContext';

// THE LINKS, one row per submodel, in the Find the links box: where links are found, they are
// also typed. What each link holds (its materials, its pictures) is the Web listings table's,
// under it.
//
// Every link is equal: each is read on its own on the next run. Adding or editing a link never
// searches on its own; nothing is read until a run. Leaving them all empty changes nothing.
export function ListingLinksTable({ docId, disabled }: { docId: string; disabled?: boolean }) {
  const { t } = useLanguage();
  const submodels = useSubmodels(docId);

  // Always keep one row to paste into — the list starts empty for a new notice, and
  // removing the last row leaves nothing to type in. An empty row reads as no listing.
  useEffect(() => {
    if (submodels.length === 0) addSubmodel(docId);
  }, [submodels.length, docId]);

  return (
    <div className="flex w-full max-w-xl flex-col items-center gap-3">
      <div className="w-full overflow-hidden rounded-xl border-2 border-slate-300 bg-white shadow-sm">
        <table className="w-full text-center text-sm">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="px-3 py-2 font-semibold text-slate-900">{t('notice.listing.link')}</th>
            </tr>
          </thead>
          <tbody>
            {submodels.map((s) => (
              <tr key={s.id} className="border-t-2 border-slate-300 first:border-t-0">
                {/* Its errors show under the field, in the same cell. */}
                <td className="px-3 py-2">
                  <LinkRow
                    value={s.url}
                    onCommit={(val) => setSubmodelUrl(docId, s.id, val)}
                    // A link removed is a submodel removed. The last row comes straight back
                    // empty (the effect above), to paste into.
                    onRemove={() => removeSubmodel(docId, s.id)}
                    detecting={s.detecting}
                    disabled={disabled}
                    placeholder={t('notice.listing.placeholder')}
                    openTitle={t('notice.listing.open')}
                    removeTitle={t('notice.listing.removeLink')}
                    error={s.error}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Another submodel: another row. */}
      <button type="button" onClick={() => addSubmodel(docId)} disabled={disabled} className={smallBtnClass}>
        <Plus className="h-3.5 w-3.5" />
        {t('notice.listing.addVariant')}
      </button>
    </div>
  );
}
